import * as THREE from 'three'

/**
 * Handles crash animations: gear explodes off the skier,
 * then after a delay the skier respawns.
 */

interface DebrisPiece {
  mesh: THREE.Mesh
  velocity: THREE.Vector3
  rotationSpeed: THREE.Vector3
  life: number
}

export class CrashSystem {
  private scene: THREE.Scene
  private debris: DebrisPiece[] = []
  private _isCrashing = false
  private crashTimer = 0
  private respawnDelay = 2.5  // seconds before respawn
  private onRespawn: (() => void) | null = null

  constructor(scene: THREE.Scene) {
    this.scene = scene
  }

  get isCrashing(): boolean {
    return this._isCrashing
  }

  /**
   * Trigger a crash at the given position.
   * Hides the skier model and spawns flying debris.
   */
  triggerCrash(
    position: THREE.Vector3,
    velocity: THREE.Vector3,
    skierModel: THREE.Group,
    onRespawn: () => void
  ): void {
    if (this._isCrashing) return

    this._isCrashing = true
    this.crashTimer = 0
    this.onRespawn = onRespawn

    // Hide skier
    skierModel.visible = false

    // Spawn debris pieces (ski gear flying everywhere)
    const debrisConfigs = [
      // Left ski (red, flat)
      { color: 0xcc0000, size: [0.1, 0.03, 0.8], name: 'ski' },
      // Right ski
      { color: 0xcc0000, size: [0.1, 0.03, 0.8], name: 'ski' },
      // Left pole (thin cylinder)
      { color: 0x999999, size: [0.03, 0.03, 0.6], name: 'pole' },
      // Right pole
      { color: 0x999999, size: [0.03, 0.03, 0.6], name: 'pole' },
      // Helmet
      { color: 0xe0e0e0, size: [0.35, 0.35, 0.35], name: 'helmet' },
      // Left glove
      { color: 0x1a1a1a, size: [0.12, 0.12, 0.12], name: 'glove' },
      // Right glove
      { color: 0x1a1a1a, size: [0.12, 0.12, 0.12], name: 'glove' },
      // Goggle lens
      { color: 0xff6600, size: [0.2, 0.1, 0.02], name: 'goggles' },
    ]

    for (const cfg of debrisConfigs) {
      const geo = new THREE.BoxGeometry(cfg.size[0], cfg.size[1], cfg.size[2])
      const mat = new THREE.MeshStandardMaterial({
        color: cfg.color,
        roughness: 0.5,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.castShadow = true
      mesh.position.copy(position)
      mesh.position.y += 1 + Math.random() * 0.5

      this.scene.add(mesh)

      // Random explosive velocity
      const speed = 3 + Math.random() * 6
      const angle = Math.random() * Math.PI * 2
      const upSpeed = 4 + Math.random() * 5

      this.debris.push({
        mesh,
        velocity: new THREE.Vector3(
          Math.cos(angle) * speed + velocity.x * 0.3,
          upSpeed,
          Math.sin(angle) * speed + velocity.z * 0.3
        ),
        rotationSpeed: new THREE.Vector3(
          (Math.random() - 0.5) * 10,
          (Math.random() - 0.5) * 10,
          (Math.random() - 0.5) * 10
        ),
        life: 2.5,
      })
    }
  }

  /**
   * Update debris physics and check for respawn.
   */
  update(dt: number, skierModel: THREE.Group): void {
    if (!this._isCrashing) return

    this.crashTimer += dt

    // Update debris
    for (const piece of this.debris) {
      piece.life -= dt

      // Gravity
      piece.velocity.y -= 15 * dt

      // Move
      piece.mesh.position.add(
        piece.velocity.clone().multiplyScalar(dt)
      )

      // Spin
      piece.mesh.rotation.x += piece.rotationSpeed.x * dt
      piece.mesh.rotation.y += piece.rotationSpeed.y * dt
      piece.mesh.rotation.z += piece.rotationSpeed.z * dt

      // Fade out near end of life
      if (piece.life < 0.5) {
        const mat = piece.mesh.material as THREE.MeshStandardMaterial
        mat.transparent = true
        mat.opacity = piece.life / 0.5
      }
    }

    // Remove dead debris
    this.debris = this.debris.filter((piece) => {
      if (piece.life <= 0) {
        this.scene.remove(piece.mesh)
        piece.mesh.geometry.dispose()
        ;(piece.mesh.material as THREE.Material).dispose()
        return false
      }
      return true
    })

    // Respawn after delay
    if (this.crashTimer >= this.respawnDelay) {
      this._isCrashing = false
      skierModel.visible = true

      // Clean up any remaining debris
      for (const piece of this.debris) {
        this.scene.remove(piece.mesh)
        piece.mesh.geometry.dispose()
        ;(piece.mesh.material as THREE.Material).dispose()
      }
      this.debris = []

      if (this.onRespawn) {
        this.onRespawn()
        this.onRespawn = null
      }
    }
  }
}
